import { CmsOffer, GiftListItem } from '../../../../../../../types/cmsProduct';
import { filterGiftsByAmountAndTenure } from '../../../../../../../utils/helpers';
import { formatAmount } from '../../../formatAmount';

/** One choose-one set of an offer's gifts at the current selection. */
export type OfferGiftSet = {
  key: string;
  /** N of "Choose 1 of N"; absent for a single gift or a bundle. */
  chooseOneOf?: number;
  gifts: { key: string; title: string; worth?: string; topup?: string }[];
};

const criteriaKey = (gift: GiftListItem): string =>
  (gift.criteria_list ?? [])
    .map((criterion) =>
      [criterion.name?.name, criterion.value_start, criterion.value_end].join(
        ':',
      ),
    )
    .sort()
    .join('|');

// The offer's gifts matching the amount and tenure. Gifts sharing the same
// criteria form one choose-one set (CMS-2023 mapping row 24); a set of several
// gifts reads "Choose 1 of N" unless the offer combines its gifts (row 25).
// A gift's worth is its set price; a gift without one shows no worth. A top-up
// the customer pays for the gift is shown as the CC offers show it (label and
// amount, `getGiftLabels`), which is why the offer's "up to" value, net of the
// top-up, can be lower than the gift's worth.
export const getGiftSets = (
  offer: CmsOffer,
  amount: number,
  tenure: number,
  currency: string,
): OfferGiftSet[] => {
  const { matchingAmountTenure } = filterGiftsByAmountAndTenure(
    offer.list_of_gifts,
    amount,
    tenure,
  );

  const sets = new Map<string, GiftListItem[]>();
  matchingAmountTenure.forEach((gift) => {
    const key = criteriaKey(gift);
    sets.set(key, [...(sets.get(key) ?? []), gift]);
  });

  return [...sets.entries()].map(([key, gifts]) => ({
    key,
    chooseOneOf:
      gifts.length > 1 && !offer.combine_gift ? gifts.length : undefined,
    gifts: gifts.map((gift, index) => {
      const value = gift.gift?.gift_value;
      return {
        key: `${gift.id}-${index}`,
        title: gift.gift?.gift_title ?? '',
        worth: value?.price ? formatAmount(value.price, currency) : undefined,
        topup:
          value?.topup_label && value.topup_amount
            ? `${value.topup_label} ${formatAmount(value.topup_amount, currency)}`
            : undefined,
      };
    }),
  }));
};
